import { useState, useEffect } from "react";
import { db } from "../firebase";
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, limit, orderBy, query, serverTimestamp, setDoc } from "firebase/firestore";
import bundledContent from "../data/content.json";

// Module-level cache — shared across all components in a session.
const contentCache = new Map();
const LS_TTL = 10 * 60 * 1000; // 10 minutes

function getCached(pageName) {
    // 1. Check in-memory first (fastest)
    if (contentCache.has(pageName)) return contentCache.get(pageName);

    // 2. Fall back to localStorage for returning visitors
    try {
        const raw = localStorage.getItem(`pageContent_${pageName}`);
        if (raw) {
            const { data, ts } = JSON.parse(raw);
            if (Date.now() - ts < LS_TTL) {
                contentCache.set(pageName, data); // warm the in-memory cache too
                return data;
            }
        }
    } catch {}
    return null;
}

function setCached(pageName, data) {
    contentCache.set(pageName, data);
    try {
        localStorage.setItem(`pageContent_${pageName}`, JSON.stringify({ data, ts: Date.now() }));
    } catch {}
}

function cloneContent(value) {
    try {
        return structuredClone(value);
    } catch {
        return JSON.parse(JSON.stringify(value));
    }
}

/**
 * Hook to fetch and manage page content from Firestore
 * @param {string} pageName - The page identifier (e.g., "about", "home")
 * @returns {object} { content, loading, error, updateContent }
 */
export function usePageContent(pageName, { fetchVersions: shouldFetchVersions = false } = {}) {
    const cached = getCached(pageName);
    const bundled = bundledContent[pageName] || null;
    const initial = cached || bundled || null;
    const [content, setContent] = useState(initial);
    const [loading, setLoading] = useState(!initial);
    const [error, setError] = useState(null);
    const [versions, setVersions] = useState([]);
    const [versionsLoading, setVersionsLoading] = useState(shouldFetchVersions);

    // Fetch content on mount
    useEffect(() => {
        async function fetchContent() {
            // Already cached — skip Firestore
            if (getCached(pageName)) return;

            try {
                setLoading(true);
                setError(null);
                const docRef = doc(db, "pageContent", pageName);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setCached(pageName, data);
                    setContent(data);
                } else {
                    setError(`No content found for page: ${pageName}`);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        }

        async function fetchVersions() {
            try {
                setVersionsLoading(true);
                const versionsRef = collection(db, "pageContentHistory", pageName, "versions");
                const versionsQuery = query(versionsRef, orderBy("createdAt", "desc"), limit(10));
                const versionsSnap = await getDocs(versionsQuery);

                setVersions(
                    versionsSnap.docs.map((versionDoc) => ({
                        id: versionDoc.id,
                        ...versionDoc.data(),
                    }))
                );
            } catch (err) {
                setVersions([]);
            } finally {
                setVersionsLoading(false);
            }
        }

        fetchContent();
        if (shouldFetchVersions) fetchVersions();
    }, [pageName]);

    // Function to update content
    const updateContent = async (newContent, userEmail, options = {}) => {
        try {
            // Validation
            if (!newContent || typeof newContent !== 'object') {
                throw new Error("Content must be a valid object");
            }

            if (!userEmail) {
                throw new Error("User email required");
            }

            if (!pageName) {
                throw new Error("Page name required");
            }

            const shouldSnapshot = options.saveSnapshot !== false;
            const docRef = doc(db, "pageContent", pageName);

            await setDoc(docRef, {
                ...newContent,
                page: pageName,
                lastUpdated: serverTimestamp(),
                updatedBy: userEmail,
            }, { merge: true });

            if (shouldSnapshot) {
                const versionsRef = collection(db, "pageContentHistory", pageName, "versions");
                // Drop lastUpdated — it can be a Firestore Timestamp or Date depending on
                // where `content` came from, and addDoc() rejects those as "a custom Date
                // object" when nested inside a map. The version's own createdAt covers this.
                const { lastUpdated, ...mergedContent } = { ...(content || {}), ...newContent };
                const contentSnapshot = cloneContent({
                    ...mergedContent,
                    page: pageName,
                    updatedBy: userEmail,
                });

                await addDoc(versionsRef, {
                    page: pageName,
                    content: contentSnapshot,
                    createdAt: serverTimestamp(),
                    createdBy: userEmail,
                });
            }

            setError(null);
            const updated = {
                ...(content || {}),
                ...newContent,
                lastUpdated: new Date(),
                updatedBy: userEmail,
            };
            setCached(pageName, updated);
            setContent(updated);
            if (shouldSnapshot) {
                setVersions(prev => [
                    {
                        id: `local-${Date.now()}`,
                        page: pageName,
                        content: cloneContent({
                            ...(content || {}),
                            ...newContent,
                            page: pageName,
                            updatedBy: userEmail,
                        }),
                        createdAt: new Date(),
                        createdBy: userEmail,
                    },
                    ...prev,
                ].filter((version, index, array) => array.findIndex((item) => item.id === version.id) === index).slice(0, 10));
            }
            return { success: true };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        }
    };

    // Call this from admin after saving to force a fresh fetch on next load
    const invalidateCache = () => {
        contentCache.delete(pageName);
        try { localStorage.removeItem(`pageContent_${pageName}`); } catch {}
    };

    const deleteVersion = async (versionId) => {
        try {
            if (!versionId.startsWith("local-")) {
                const versionRef = doc(db, "pageContentHistory", pageName, "versions", versionId);
                await deleteDoc(versionRef);
            }
            setVersions(prev => prev.filter(v => v.id !== versionId));
            return { success: true };
        } catch (err) {
            return { success: false, error: err.message };
        }
    };

    return { content, loading, error, updateContent, invalidateCache, deleteVersion, versions, versionsLoading };
}
